import React from "react";
import { formatTimeFromNow } from "../util/time";
import useOrganisation from "./useOrganisation";

const getValidDate = (value) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date;
};

function useTrialExpiry(showLabelPrompt) {
  const [prompt, setPrompt] = React.useState(null);
  const { subscription, sandbox } = useOrganisation(null, { suspense: false });

  const {
    isInTrial,
    trialExpiryDate,
    isExpiredTrial,
    isSubscribed,
    isCancelled,
    isSuspended,
    moduleSubscriptions,
  } = subscription || {};

  const now = new Date();

  // Include both active trials and expired trials
  const trialModules = (moduleSubscriptions || []).filter((module) => {
    if (module?.isInTrial) return true;
    // Also include modules with expired trials
    const trialEndDate = getValidDate(module?.trialEndsAt);
    return trialEndDate && trialEndDate < now && !module?.isActive;
  });

  const trialModulesWithDates = trialModules
    .map((module) => ({
      module,
      trialEndDate: getValidDate(module?.trialEndsAt),
    }))
    .filter((entry) => Boolean(entry.trialEndDate))
    .sort((a, b) => a.trialEndDate.getTime() - b.trialEndDate.getTime());

  const moduleTrialDates = trialModulesWithDates.map(
    (entry) => entry.trialEndDate,
  );

  const baseTrialDate = getValidDate(trialExpiryDate);
  const primaryTrialDate = moduleTrialDates[0] || baseTrialDate;

  const activeTrialModules = (moduleSubscriptions || []).filter((module) =>
    Boolean(module?.isInTrial),
  );
  const hasLiveActiveSubscription = (moduleSubscriptions || []).some(
    (module) => Boolean(module?.isActive) && !Boolean(module?.isInTrial),
  );
  const isModuleTrialActive = activeTrialModules.length > 0;
  const isModuleTrialExpired = moduleTrialDates.some((date) => date < now);

  const resolvedIsInTrial = Boolean(isInTrial || isModuleTrialActive);
  const resolvedIsExpiredTrial = Boolean(
    isExpiredTrial || isModuleTrialExpired,
  );
  const trialExpired =
    resolvedIsExpiredTrial ||
    Boolean(primaryTrialDate && primaryTrialDate < now && resolvedIsInTrial);

  React.useEffect(() => {
    const prompts = [
      `Don't delay getting paid`,
      "Bring revenue in faster",
      "Save time and get paid faster",
      "Get paid for your work",
    ];

    if (!prompt) {
      const newPrompt = prompts[Math.floor(Math.random() * prompts.length)];
      setPrompt(newPrompt);
    }
  }, [prompt]);

  const expiredLabel =
    trialExpired && primaryTrialDate && primaryTrialDate < now
      ? "expired"
      : "expires";
  const expiredTimeFromNow = primaryTrialDate
    ? formatTimeFromNow(primaryTrialDate)
    : null;
  const labelPrompt = showLabelPrompt ? `. ${prompt}` : "";

  const activeModuleCount = activeTrialModules.length;
  const expiredModuleCount = trialModules.length - activeModuleCount;
  const primaryTrialModule =
    trialModulesWithDates[0]?.module || trialModules[0];
  const primaryModuleName = primaryTrialModule?.moduleName || "Module";

  const trialBaseLabel = (() => {
    if (!expiredTimeFromNow) {
      if (activeModuleCount > 1)
        return `${activeModuleCount} modules are in trial`;
      if (activeModuleCount === 1) return `${primaryModuleName} is in trial`;
      if (expiredModuleCount > 0)
        return `${expiredModuleCount} module${expiredModuleCount > 1 ? "s" : ""} trial expired`;
      return "Your trial is active";
    }

    if (activeModuleCount > 1) {
      return `${activeModuleCount} modules are in trial. Next trial ${expiredLabel} ${expiredTimeFromNow}`;
    }

    if (activeModuleCount === 1) {
      return `${primaryModuleName} trial ${expiredLabel} ${expiredTimeFromNow}`;
    }

    if (expiredModuleCount > 0) {
      return `${primaryModuleName} trial ${expiredLabel} ${expiredTimeFromNow}`;
    }

    return `Trial ${expiredLabel} ${expiredTimeFromNow}`;
  })();

  const trialExpiryLabel = `${trialBaseLabel}${labelPrompt}`;
  const sandboxExpiryLabel = expiredTimeFromNow
    ? `Your demo organisation ${expiredLabel} ${expiredTimeFromNow}. Connect your accounting software`
    : "Your demo organisation trial is active. Connect your accounting software";
  const expiryLabelText = sandbox ? sandboxExpiryLabel : trialExpiryLabel;
  const expiryLabel = !(isCancelled || isSuspended) ? expiryLabelText : "";

  return {
    isInTrial: resolvedIsInTrial,
    isExpiredTrial: resolvedIsExpiredTrial,
    hasLiveActiveSubscription,
    isSubscribed,
    isCancelled,
    isSuspended,
    expiryLabel,
  };
}

export default useTrialExpiry;
